import React from 'react'
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { MsalConsumer } from '@azure/msal-react'
import { loginRequest } from '../../config/msalConfig'

let popupPromise = null

/**
 * Builds an object shaped like Auth0's useAuth0() result so existing class components
 * can keep using the `auth0` prop with minimal changes.
 *
 * @param {object} msalContext - The context provided by MsalConsumer.
 * @param {import('@azure/msal-browser').IPublicClientApplication} msalContext.instance
 * @param {import('@azure/msal-browser').AccountInfo[]} msalContext.accounts
 * @param {string} msalContext.inProgress
 * @returns {object} An Auth0-shaped adapter exposing `getAccessTokenSilently`
 *   and `getAccessTokenWithPopup`.
 */
export const createAuth0Adapter = msalContext => {
  const { instance, accounts, inProgress } = msalContext

  const getScopesFromOptions = options => {
    if (options && options.audience) {
      return [options.audience]
    }
    return loginRequest.scopes
  }

  const acquireTokenWithPopupOnce = (scopes, account) => {
    if (popupPromise) return popupPromise

    const request = { scopes, redirectUri: `${window.location.origin}/blank.html` }

    // First sign-in: loginPopup. Later interactive renewals: acquireTokenPopup.
    if (account) {
      popupPromise = instance.acquireTokenPopup({ ...request, account }).finally(() => {
        popupPromise = null
      })
    } else {
      popupPromise = instance
        .loginPopup(request)
        .then(response => {
          if (response?.account) {
            instance.setActiveAccount(response.account)
          }
          return response
        })
        .finally(() => {
          popupPromise = null
        })
    }

    return popupPromise
  }

  const markInteractionRequired = error => {
    error.interactionRequired = true
    return error
  }

  /**
   * Resolves an access token, requesting user interaction (a popup) only when
   * a silent renewal is not possible and the caller allows it.
   *
   * @param {object} [options]
   * @param {string} [options.audience] - OAuth audience to scope the request to.
   * @param {boolean} [options.silentOnly] - When `true`, never fall back to the
   *   interactive popup: rethrow the original error tagged with
   *   `interactionRequired: true` instead. Intended for background refreshes
   *   (Apollo's error link), where a popup with no user gesture is blocked by
   *   the browser or steals focus from whatever the user is doing.
   * @returns {Promise<string>} The access token.
   * @throws {Error} With `interactionRequired: true` when no MSAL account
   *   exists, or when a silent renewal needs interaction and `silentOnly` is
   *   set. Any other MSAL failure is rethrown as-is.
   */
  const getAccessTokenSilently = async (options = {}) => {
    const account = accounts[0] || instance.getActiveAccount()
    const scopes = getScopesFromOptions(options)

    const silentRequest = {
      scopes,
      account,
      redirectUri: `${window.location.origin}/blank.html`,
    }

    if (!account) {
      throw markInteractionRequired(new Error('Login required'))
    }

    try {
      const response = await instance.acquireTokenSilent(silentRequest)
      return response.accessToken
    } catch (e) {
      const needsInteraction =
        e instanceof InteractionRequiredAuthError ||
        e?.name === 'InteractionRequiredAuthError' ||
        e?.errorCode === 'no_account_found' ||
        e?.errorCode === 'no_tokens_found'
      if (!needsInteraction) throw e
      if (options.silentOnly) throw markInteractionRequired(e)
      const response = await acquireTokenWithPopupOnce(scopes, account)
      return response.accessToken
    }
  }

  const getAccessTokenWithPopup = async (options = {}) => {
    const scopes = getScopesFromOptions(options)
    const account = accounts[0] || instance.getActiveAccount()
    const response = await acquireTokenWithPopupOnce(scopes, account)
    return response.accessToken
  }

  return {
    instance,
    accounts,
    isLoading: inProgress !== InteractionStatus.None,
    error: null,
    getAccessTokenSilently: options => getAccessTokenSilently(options || {}),
    getAccessTokenWithPopup: options => getAccessTokenWithPopup(options || {}),
  }
}

/**
 * Drop-in replacement for @auth0/auth0-react's withAuth0 — injects `auth0` prop.
 */
export const withMsal = Component => {
  const Wrapped = props => (
    <MsalConsumer>
      {msalContext => <Component {...props} auth0={createAuth0Adapter(msalContext)} />}
    </MsalConsumer>
  )

  Wrapped.displayName = `withMsal(${Component.displayName || Component.name || 'Component'})`

  return Wrapped
}
